import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { BarChart3, Package, FolderOpen, FileText, Users, Settings as SettingsIcon, MessageSquare, TrendingUp, Brain, LogOut } from 'lucide-react';
import api, { isAuthenticated, setAuthToken } from '../utils/api';

const AdminDashboard = () => {
  const [stats, setStats] = useState({ total_products: 0, total_categories: 0, total_customers: 0, total_blogs: 0 });
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/admin/login');
      return;
    }
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get('/statistics/overview');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleLogout = () => {
    setAuthToken(null);
    localStorage.removeItem('admin_user');
    navigate('/admin/login');
  };

  const menuItems = [
    { icon: Package, label: 'Products', path: '/admin/products', color: 'bg-blue-500' },
    { icon: FolderOpen, label: 'Categories', path: '/admin/categories', color: 'bg-green-500' },
    { icon: FileText, label: 'Blogs', path: '/admin/blogs', color: 'bg-purple-500' },
    { icon: Users, label: 'CRM', path: '/admin/crm', color: 'bg-pink-500' },
    { icon: BarChart3, label: 'Statistics', path: '/admin/statistics', color: 'bg-orange-500' },
    { icon: Brain, label: 'Smart Dashboard', path: '/admin/smart-dashboard', color: 'bg-indigo-500' },
    { icon: MessageSquare, label: 'Homepage Content', path: '/admin/content', color: 'bg-teal-500' },
    { icon: SettingsIcon, label: 'Settings', path: '/admin/settings', color: 'bg-gray-500' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Bar */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <img
              src="https://customer-assets.emergentagent.com/job_ae32ccb0-3c5f-414a-b46c-cfd49c04a91f/artifacts/kjz9vxob_556719997_17915806479189788_8925177708886366329_n.jpg"
              alt="Ahmedabad NX"
              className="h-10 w-10"
            />
            <h1 className="font-heading text-xl font-bold text-[#2A0000]">Admin Dashboard</h1>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center space-x-2 text-red-600 hover:text-red-700"
            data-testid="logout-button"
          >
            <LogOut className="w-5 h-5" />
            <span>Logout</span>
          </button>
        </div>
      </header>

      <div className="p-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-600">Total Products</h3>
              <Package className="w-5 h-5 text-blue-500" />
            </div>
            <p className="text-3xl font-bold text-[#2A0000]">{stats.total_products}</p>
          </div>

          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-600">Categories</h3>
              <FolderOpen className="w-5 h-5 text-green-500" />
            </div>
            <p className="text-3xl font-bold text-[#2A0000]">{stats.total_categories}</p>
          </div>

          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-600">Customers</h3>
              <Users className="w-5 h-5 text-pink-500" />
            </div>
            <p className="text-3xl font-bold text-[#2A0000]">{stats.total_customers}</p>
          </div>

          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-600">Blog Posts</h3>
              <FileText className="w-5 h-5 text-purple-500" />
            </div>
            <p className="text-3xl font-bold text-[#2A0000]">{stats.total_blogs}</p>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-[#2A0000] mb-4">Quick Actions</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Link to="/admin/products/new" className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow text-center">
              <Package className="w-8 h-8 mx-auto mb-2 text-blue-500" />
              <p className="font-medium text-[#2C2C2C]">Add Product</p>
            </Link>
            <Link to="/admin/customers/new" className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow text-center">
              <Users className="w-8 h-8 mx-auto mb-2 text-pink-500" />
              <p className="font-medium text-[#2C2C2C]">Add Customer</p>
            </Link>
            <Link to="/admin/blogs/new" className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow text-center">
              <FileText className="w-8 h-8 mx-auto mb-2 text-purple-500" />
              <p className="font-medium text-[#2C2C2C]">Write Blog</p>
            </Link>
            <Link to="/admin/crm" className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow text-center">
              <MessageSquare className="w-8 h-8 mx-auto mb-2 text-green-500" />
              <p className="font-medium text-[#2C2C2C]">View CRM</p>
            </Link>
          </div>
        </div>

        {/* Main Menu */}
        <div>
          <h2 className="text-2xl font-bold text-[#2A0000] mb-4">Management Modules</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {menuItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm hover:shadow-lg transition-all group"
                data-testid={`menu-${item.label.toLowerCase()}`}
              >
                <div className={`w-12 h-12 ${item.color} rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <item.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-semibold text-[#2A0000] mb-1">{item.label}</h3>
                <p className="text-sm text-gray-600">Manage {item.label.toLowerCase()}</p>
              </Link>
            ))}
          </div>
        </div>

        {/* Info Box */}
        <div className="mt-8 bg-[#E5A93C]/10 border border-[#E5A93C]/30 rounded-lg p-6">
          <h3 className="font-bold text-[#2A0000] mb-2">🎉 Welcome to Ahmedabad NX Admin Panel</h3>
          <p className="text-[#2C2C2C] text-sm">
            This comprehensive dashboard gives you full control over products, customers, content, and analytics. 
            Use the CRM for WhatsApp broadcasts and the Smart Dashboard for AI-powered customer insights.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
